import { GENERAL } from "./Config";
import { BARE_SPEED, sailPower, WIND_SPEED } from "./Wind";

/**
 * How a hull moves, and how big the sea is. Imported by both the client (for prediction
 * and for the attract-mode boat) and the server (for authority) — see
 * design/Networking.md. Two implementations of "how a boat turns" would drift, and drift
 * shows up as ships snapping sideways.
 *
 * Everything here is per second, matching the rest of the repo.
 *
 * The numbers marked *tunable* come from `config/general.json` by way of
 * [Config.ts](Config.ts) and are edited in the dashboard (`npm run editor`, see
 * design/Settings.md). They are still `const` and still read once at module load — the
 * indirection is only about where the number was typed, not about when it can change.
 */

/** Cruise speed, in pixels per second. **Tunable.** */
export const CRUISE_SPEED = GENERAL.boatBaseSpeed;
/**
 * Extra speed while the turbo is lit, in pixels per second, on top of cruise. **Tunable.**
 * TURN_RATE is deliberately **not** raised to match — a hull under turbo covers more ground
 * per radian, so its turning circle widens even before TURN_SPEED_FACTOR takes a bite out of
 * the rudder as well. Going faster therefore costs manoeuvring, which is the same bargain
 * design/Pitch.md makes about turning being committal. What it costs in points is
 * design/Battle.md.
 */
export const TURBO_SPEED_INCREMENT = GENERAL.turboSpeedIncrement;
/** Flat-out speed, in pixels per second. */
export const TURBO_SPEED = CRUISE_SPEED + TURBO_SPEED_INCREMENT;
/**
 * Turbo speed as a multiple of cruise — the form design/Battle.md quotes and the tests
 * measure against. Derived, because the dashboard tunes the increment.
 */
export const TURBO_SPEED_MULTIPLIER = TURBO_SPEED / CRUISE_SPEED;
/**
 * How fast a hull comes about, in radians per second, at its own cruise speed. Turning is
 * committal by design. This is a *decision*, not a dial (design/Settings.md): what people want
 * to drag is how much speed costs, and that is TURN_SPEED_FACTOR below.
 */
export const TURN_RATE = 0.9;
/**
 * How much of a hull's speed is paid for in manoeuvrability. **Tunable.**
 *
 * Zero is the arena as it was before the dial existed: a boosting hull turned at the same
 * radians per second and simply carved a wider arc, because it covered more ground per radian.
 * Above zero the rudder itself goes heavy with speed as well — see `turnScaleAtSpeed`.
 */
export const TURN_SPEED_FACTOR = GENERAL.turnSpeedFactor;

/**
 * What a hull making `speedRatio` times **its own** cruise speed keeps of TURN_RATE.
 *
 * Deliberately measured against the hull's own cruise and not against CRUISE_SPEED: a Galleon
 * is slower *and* more committal than a Sloop by its `Handling` (design/Evolution.md), and
 * scaling off the global base would hand that hull a turn bonus for being slow and quietly
 * flatten the ladder. What this models is the extra speed a hull is carrying over its own
 * natural pace — which today means the turbo, and tomorrow whatever else lights the sails.
 *
 * The curve is `1 / (1 + factor * (ratio - 1))`: one at cruise for any factor, falling off
 * hyperbolically so it never reaches zero and never turns the rudder backwards. Ratios below
 * one are clamped away rather than paying a bonus — going slower than usual is not a manoeuvring
 * advantage, it is just slow.
 *
 * Turning **radius** is speed over turn rate, so the widening a captain actually feels is
 * `ratio / turnScaleAtSpeed(ratio)` — at the default 1.8x turbo and a factor of 0.5 that is a
 * circle 2.5 times wider, against 1.8 times when the factor is zero.
 */
export function turnScaleAtSpeed(speedRatio: number): number {
    return 1 / (1 + TURN_SPEED_FACTOR * (Math.max(1, speedRatio) - 1));
}

/**
 * What a hull with the turbo lit and nothing else going on keeps of TURN_RATE. Derived, and no
 * longer what the simulation asks for — `turnScaleOf` reads a hull's *actual* speed now, and the
 * turbo is only one of the two things that sets it. Kept because it is the figure the dashboard
 * quotes and design/Settings.md is written against: what the boost costs a hull at plain cruise.
 */
export const TURBO_TURN_SCALE = turnScaleAtSpeed(TURBO_SPEED_MULTIPLIER);

/**
 * What a hull making `speed` keeps of TURN_RATE, measured against **its own** cruise.
 *
 * This is where `turnScaleAtSpeed`'s promise about "whatever else lights the sails" is collected:
 * the input used to be a boolean, because the turbo was the only way to exceed cruise. Now the
 * wind sets a hull's speed too, so the rule reads the speed itself and the turbo is just one of
 * the things that raises it.
 *
 * Below cruise the curve is clamped flat, which matters more than it looks: a hull in irons turns
 * at exactly the rate it always did. A rudder that went heavy as the sails emptied would make a
 * bad heading an inescapable one — slow because pointed wrong, unable to fix the heading because
 * slow — which is the real-life failure mode this deliberately does not model. See
 * design/Wind.md.
 */
export function turnScaleOf(handling: Handling, speed: number): number {
    return turnScaleAtSpeed(speed / (CRUISE_SPEED * handling.speed));
}

/**
 * Width and height of the arena, in world pixels. **Tunable.** The world is a torus:
 * sailing off the west edge arrives at the east one, so there is no boundary and no map
 * edge to hide in.
 */
export const ARENA_SIZE = GENERAL.mapSize;
/**
 * How long it takes to sail the full width of the arena at cruise. Derived, and the number
 * to judge a map-size change by: the tuning question is "how many seconds across is the
 * sea", and the pixel count is only how that is spelled.
 */
export const ARENA_CROSSING_SECONDS = ARENA_SIZE / CRUISE_SPEED;

/** Physics rate on the server, in Hz. Ship handling wants to be smooth and deterministic. */
export const SIMULATION_HZ = 60;
/**
 * How often a captain's helm reaches the arena, in Hz — one `HelmInput` per step, applied once and
 * then integrated at SIMULATION_HZ through the sub-steps below.
 *
 * **Half the physics rate, deliberately.** A predicted client sends one input per step whether or
 * not anything moved, because a gap in the stream is a gap in the replay, so this is the upstream
 * packet rate per player and 60 would be twice the traffic for a resolution finer than the 20 Hz
 * patches can report back. What it must *not* do is change how a hull moves — hence the sub-steps.
 */
export const INPUT_HZ = 30;
/**
 * Physics sub-steps per input step. Derived, and the reason `INPUT_HZ` is free to move: the
 * integrator still runs at exactly SIMULATION_HZ whatever the input rate is, so the handling that
 * every number in this file was tuned against does not depend on how often the helm is heard.
 *
 * Both ends must agree, and neither writes it down twice — the arena declares it on
 * `setFixedTimestep` and Colyseus advertises it to the client in the join handshake. See
 * design/Networking.md.
 */
export const SUB_STEPS = SIMULATION_HZ / INPUT_HZ;
/** How often state deltas go out, in ms. Clients interpolate between these. */
export const PATCH_RATE_MS = 50;
/** Arena cap. Kept low enough that full-state sync stays cheap — see design/Networking.md. */
export const MAX_CLIENTS = 50;

/** Signed shortest angle from `from` to `to`, in (-PI, PI]. */
export function shortestAngle(from: number, to: number): number {
    return Math.atan2(Math.sin(to - from), Math.cos(to - from));
}

/**
 * Folds an angle back into (-PI, PI]. `turnToward` converges on whichever representative
 * of the target is nearest, so without this a heading random-walks by a full turn at a
 * time and eventually costs float32 precision on the wire.
 */
export function normaliseAngle(radians: number): number {
    return Math.atan2(Math.sin(radians), Math.cos(radians));
}

/** Folds a world coordinate back into [0, ARENA_SIZE). Correct for negative values. */
export function wrapCoord(value: number): number {
    return ((value % ARENA_SIZE) + ARENA_SIZE) % ARENA_SIZE;
}

/**
 * Shortest signed distance from `from` to `to` on the looping axis, in (-ARENA_SIZE/2,
 * ARENA_SIZE/2]. This is what makes the wrap invisible: a ship one pixel west of the west
 * edge is one pixel away from a camera at the east edge, not a whole arena away.
 */
export function wrapDelta(from: number, to: number): number {
    const half = ARENA_SIZE / 2;
    return wrapCoord(to - from + half) - half;
}

/**
 * Swings `heading` towards `targetHeading` at TURN_RATE — scaled by `turn`, which is the
 * hull's own multiplier — and returns the new heading. Clamped to the remaining angle so a hull
 * settles on its course instead of overshooting and swinging back on the next step.
 */
export function turnToward(heading: number, targetHeading: number, deltaSeconds: number, turn = 1): number {
    const delta = shortestAngle(heading, targetHeading);
    return heading + Math.sign(delta) * Math.min(Math.abs(delta), TURN_RATE * turn * deltaSeconds);
}

/** A thing with a place and a facing in the sea. Both `Ship` and the client's prediction fit. */
export interface Sailing {
    x: number;
    y: number;
    heading: number;
}

/**
 * How a particular hull handles, as multiples of the two constants above. A `Fitout` carries
 * exactly this pair (`shared/Fitout.ts`), which is how a hull level reaches the integrator
 * without `shared/Sailing.ts` having to know what a boat tree is.
 *
 * The default is the hull everybody spawns on, authored at 1x and 1x, so a caller that has no
 * fitout to hand — the attract-screen boat, a test — sails exactly as the arena always did.
 */
export interface Handling {
    /** Cruise and turbo speed, scaled. Below 1 is a hull that is harder to run down with. */
    speed: number;
    /** Turn rate, scaled. Below 1 is a hull that is more committal to come about in. */
    turn: number;
    /**
     * How much of the wind this ship's canvas collects — the **rig** rather than the hull, and the
     * only member of this trio that is not a property of the hull level.
     *
     * It rides here because a `Fitout` resolves the whole ship and this is the one integrator both
     * ends run: what `advance()` needs is everything about a vessel that decides how it moves, and
     * splitting the rig into a fourth parameter would only make the call longer. 1 is bare canvas
     * — the wind is worth WIND_SPEED and no more. See `rigDrive` in `shared/Fitout.ts`.
     */
    drive: number;
}

/** Level 1's handling: no scaling at all, and canvas worth exactly the wind. */
export const EVEN_HANDLING: Handling = { speed: 1, turn: 1, drive: 1 };

/**
 * How fast this hull is making way right now, in world pixels per second. **The one answer**, and
 * the reason there is one: `advance()` moves the ship by it and the client's rudder scales itself
 * against it, and two ways of working it out would drift the moment the weather changed either.
 *
 * ```
 * canvas = BARE_SPEED + WIND_SPEED * drive * sailPower(wind, heading)
 * speed  = (canvas + turbo increment) * hull speed
 * ```
 *
 * A rung-1 hull running dead downwind makes CRUISE_SPEED, which is what keeps every number tuned
 * before the wind existed meaning what it meant; the same hull in irons makes BARE_SPEED. The
 * turbo increment is added **before** the hull's own multiplier, exactly as it always was, so a
 * Galleon cannot reach a Sloop's flat-out speed (design/Evolution.md).
 *
 * **The sea is not in here, and that is deliberate.** Waves moved hulls for one phase and it was
 * taken back out: two things pushing a boat muddied the one that reads well, so the wind is the
 * only thing that decides how fast a hull goes. What the water does now is get looked at — see
 * `src/scripts/sea/Waves.ts` and design/Water.md.
 *
 * **`wind` of `null` is not a calm — it is a caller with no weather at all**, and it makes way at
 * plain cruise, which is the arena as it was before any of this. That is what lets anything outside
 * a battle keep sailing without inventing weather for it.
 */
export function hullSpeed(handling: Handling, turbo: boolean, wind: number | null, heading: number): number {
    const canvas = wind === null ? CRUISE_SPEED : BARE_SPEED + WIND_SPEED * handling.drive * sailPower(wind, heading);

    return (canvas + (turbo ? TURBO_SPEED_INCREMENT : 0)) * handling.speed;
}

/**
 * One step of ship movement, applied in place: come about towards `targetHeading`, make
 * way at CRUISE_SPEED — or TURBO_SPEED with `turbo` lit — and wrap at the edges of the sea.
 *
 * Heading 0 is north — straight up the screen — and grows clockwise, which is why y takes
 * the cosine negated. That matches the artwork, which is drawn bow-up (design/Ships.md).
 *
 * `turbo` is passed in rather than read off the ship because whether it is lit is a
 * different question on each side: the server owns it, and the client predicts it from the
 * button under the player's finger. Only what it does to a hull — how much faster, and how
 * much heavier at the rudder — lives here, so the two cannot disagree about that.
 *
 * `handling` is the hull's own speed and turn multipliers, and it is passed in for the same
 * reason: a bigger ship is slower and more committal (design/Evolution.md), and the *one*
 * integrator both sides run has to take that as a parameter rather than growing a second copy
 * of itself per hull. The two ends read it off the same levels on the wire, so a Galleon sails
 * like a Galleon on every screen.
 *
 * `wind` is the arena's weather, in the same radians a heading uses, and it is what makes a hull's
 * speed depend on where it is pointed — see `hullSpeed`. It is passed in rather than read off the
 * ship for the reason `turbo` is: the server has it as state and the client has its own copy of
 * the same patch, and neither is the ship's own property. **`null` means a caller with no weather
 * at all**, which sails at plain cruise.
 *
 * `wrap` is the one thing here that is not about how a hull moves, and it is a parameter for exactly
 * that reason. Folding the result into `[0, ARENA_SIZE)` is how a *position is stored*, not how a
 * ship sails, and the client's prediction needs an **unwrapped** coordinate: the netcode SDK that
 * smooths and reconciles it subtracts two numbers, and a straight subtraction across the seam is a
 * boat sprinting the width of the arena. The arena keeps wrapping — its state, its collision grid
 * and its float32 fields all depend on it — and the prediction folds only at the point of drawing.
 *
 * That is still **one integrator**: everything above this line is translation-invariant, so the two
 * trajectories differ by exactly a multiple of ARENA_SIZE and agree about every heading, speed and
 * distance. See design/Networking.md, "On the client".
 *
 * The three arguments meet at the rudder: the hull's `turn` is scaled again by `turnScaleOf`
 * against the speed it is actually making, so going fast costs manoeuvrability on top of whatever
 * the hull already handles like — and going fast is now a matter of heading as well as of the
 * turbo. Anything that steers a hull outside this function — the client's rudder, which winds a
 * wanted course round at the rate the ship could actually follow — has to run the same two calls,
 * or the course runs away from the boat and the prediction fights the arena.
 */
export function advance(
    ship: Sailing,
    targetHeading: number,
    deltaSeconds: number,
    turbo = false,
    handling: Handling = EVEN_HANDLING,
    wind: number | null = null,
    wrap = true
): void {
    // Read once, off the heading the hull *enters* the step on, and used for both halves below:
    // the water going past the rudder and the wind in the canvas are the same instant's, and
    // splitting them would be two calls to buy a fifth of a degree of heading.
    const speed = hullSpeed(handling, turbo, wind, ship.heading);

    // Normalised on the way out. Every consumer compares headings with `shortestAngle`,
    // so a hull crossing north reads as a small turn, not as a full spin.
    ship.heading = normaliseAngle(turnToward(ship.heading, targetHeading, deltaSeconds, handling.turn * turnScaleOf(handling, speed)));

    const distance = speed * deltaSeconds;
    const x = ship.x + Math.sin(ship.heading) * distance;
    const y = ship.y - Math.cos(ship.heading) * distance;

    ship.x = wrap ? wrapCoord(x) : x;
    ship.y = wrap ? wrapCoord(y) : y;
}
